import React, { useCallback, useEffect, useState } from 'react';
import styles from './index.module.scss';
import DataTable from 'components/DataTable';
import { showError } from 'utils';
import { tableStyles } from 'css/tableStyles';
import { salesChannelCountByInventoryItem } from 'services';
import { Loading } from 'components/loading/Loading';
import CustomLink from 'components/TableLink';
import _ from 'lodash';
import ListViewModal from 'components/ListViewModal';
import { imageKitBaseURL } from 'components/ImageWrapper';

const SalesChannelCountByInventoryItem = () => {
  const [data, setData] = useState<Record<string, any>[]>([]);
  const [dataToShow, setDataToShow] = useState([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isSalesChannelModalOpen, setIsSalesChannelModalOpen] = useState(false);
  const [salesChannelsList, setSalesChannelsList] = useState([]);

  const toggleModal = () => {
    setIsSalesChannelModalOpen(!isSalesChannelModalOpen);
  };

  const getSalesChannelCountByInventoryItem = async () => {
    try {
      setLoading(true);
      const reportData = await salesChannelCountByInventoryItem();
      setData(reportData);
      setDataToShow(reportData);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSalesChannelCountByInventoryItem();
  }, []);
  
  const columns = [
    {
      name: 'Item ID',
      selector: 'objectId',
      sortable: false,
      // eslint-disable-next-line react/display-name
      cell: row => (
        <CustomLink
          text={row.objectId}
          linkTo={`/inventory-items/${row.objectId}`}
        />
      ),
    },
    {
      name: 'Name',
      selector: 'name',
      sortable: true,
    },
    {
      name: 'Image',
      selector: 'image',
      sortable: true,
      // eslint-disable-next-line react/display-name
      cell: row => {
        const url = _.get(row, 'image._url', '');
        return(
          <div>
            {!!url && <img src={url} className={styles['image']} />}
          </div>
        );
      },
    },
    {
      name: 'Category',
      selector: 'category.description',
      sortable: true,
    },
    {
      name: 'Sales Channel Count',
      selector: 'salesChannelCount',
      sortable: true,
    },
    {
      name: 'Actions',
      selector: 'salesChannelCount',
      cell: row => {
        return(
          <div onClick={() => {
            setSalesChannelsList(row?.salesChannels);
            toggleModal();
          }}>
            <img src={`${imageKitBaseURL}/icons/icons8-info_7lVKzaR80.svg`} />
          </div>
        );
      },
    },
  ];


  const getDataTable = () => {
    if (loading) return <Loading type={'mini'} />;

    return (
      <DataTable
        keyField="objectId"
        columns={columns}
        noHeader={true}
        data={dataToShow}
        highlightOnHover={true}
        defaultSortField="createdAt"
        customStyles={tableStyles}
      />
    );
  };

  const searchItems = (e) => {
    const newList = data.filter((item) => {
      const name = item.name.toLowerCase();
      const category = item?.category.description.toLowerCase();
      return name.includes(e.toLowerCase()) || category.includes(e.toLowerCase());
    }) as any;
    setDataToShow(newList);
  };

  const searchItemsDebounced = useCallback(_.debounce(searchItems, 1000), [data]);

  return (
    <>
      <div className={styles['header']}>
        <div className={styles['heading']}>Sales Channel Count By Inventory Item</div>
      </div>
      <div style={{ width: 300, float: 'right' }} className='my-2'>
        <div className={styles['label']}>Name or Category</div>
        <input
          className={'form-control'}
          onChange={e => searchItemsDebounced(e.target.value)}
          placeholder={'Search'}
          type={'text'}
        />
      </div>
      {getDataTable()}
      {isSalesChannelModalOpen && 
        <ListViewModal 
          toggleModal={toggleModal} 
          title='Sales Channels' 
          list={salesChannelsList}
          renderItem={(item, index)=>{
            return (
              <div key={index}>
                {index+1}. &nbsp;
                <CustomLink text={item?.name} linkTo={`sales-channels/${item?.objectId}`} linkProps={{ target: '_blank' }} />
              </div>
            );
          }}
        />
      }
    </>
  );
};

export default SalesChannelCountByInventoryItem;